import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { programService, userService } from '../services/api';
import { Program, ProgramPricingPathway, ProgramPricingResponse } from '../types';
import { useAuth } from '../contexts/AuthContext';

interface StudentApplicationSnapshot {
  id?: number | null;
  selected_program_id?: number | null;
  program_interest?: string | null;
  step_completion?: Record<string, boolean | string>;
}

const ProgramPortalPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [application, setApplication] = useState<StudentApplicationSnapshot | null>(null);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [pricing, setPricing] = useState<ProgramPricingResponse | null>(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reviewed, setReviewed] = useState(false);
  const [savingReview, setSavingReview] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [readingProgress, setReadingProgress] = useState(0);
  const [visitedSections, setVisitedSections] = useState<Set<string>>(new Set(['hero']));

  useEffect(() => {
    let active = true;
    const loadData = async () => {
      try {
        setLoading(true);
        const [appResponse, programList] = await Promise.all([
          userService.getMyApplication(),
          programService.getPrograms(),
        ]);
        if (!active) return;
        setApplication(appResponse.application || null);
        setPrograms(programList || []);
        const reviewedFlag = Boolean(appResponse.application?.step_completion?.program_resource_portal);
        setReviewed(reviewedFlag);
      } catch (error) {
        console.error('Failed to load program portal data', error);
      } finally {
        if (active) setLoading(false);
      }
    };
    loadData();
    return () => {
      active = false;
    };
  }, []);

  const selectedProgram = useMemo(() => {
    if (!application) return null;
    if (application.selected_program_id) {
      return programs.find((program) => program.id === application.selected_program_id) || null;
    }
    if (application.program_interest) {
      return programs.find((program) => program.title === application.program_interest) || null;
    }
    return programs[0] || null;
  }, [application, programs]);

  useEffect(() => {
    const programId = selectedProgram?.id;
    if (!programId) {
      setPricing(null);
      return;
    }
    let active = true;
    setPricingLoading(true);
    programService.getProgramPricing(programId)
      .then((data) => {
        if (!active) return;
        setPricing(data);
      })
      .catch((error) => {
        console.error('Failed to load program pricing for portal', error);
        if (!active) return;
        setPricing(null);
      })
      .finally(() => {
        if (!active) return;
        setPricingLoading(false);
      });
    return () => {
      active = false;
    };
  }, [selectedProgram?.id]);

  useEffect(() => {
    if (!toastMessage) return;
    const timer = window.setTimeout(() => setToastMessage(null), 2500);
    return () => window.clearTimeout(timer);
  }, [toastMessage]);

  const sectionOrder = [
    'hero',
    'overview',
    'timing',
    'academics',
    'life',
    'structure',
    'included',
    'optional',
    'profile',
    'next',
  ];

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const storageKey = `portalVisited:${selectedProgram?.id ?? 'default'}`;
    const stored = window.localStorage.getItem(storageKey);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setVisitedSections(new Set(parsed.filter((item) => typeof item === 'string')));
        }
      } catch {
        // ignore invalid storage
      }
    }
  }, [selectedProgram?.id]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const storageKey = `portalVisited:${selectedProgram?.id ?? 'default'}`;
    window.localStorage.setItem(storageKey, JSON.stringify(Array.from(visitedSections)));
    const percent = Math.round((visitedSections.size / sectionOrder.length) * 100);
    setReadingProgress(percent);
  }, [visitedSections, sectionOrder.length, selectedProgram?.id]);

  const estimateDurationDays = (pathway: ProgramPricingPathway) => {
    if (pathway.duration_days != null) return pathway.duration_days;
    if (pathway.start_date && pathway.end_date) {
      const start = new Date(pathway.start_date);
      const end = new Date(pathway.end_date);
      if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
        const diff = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
        return diff >= 0 ? diff + 1 : null;
      }
    }
    return null;
  };

  const formatDuration = (pathway: ProgramPricingPathway) => {
    const days = estimateDurationDays(pathway);
    if (!days) return null;
    const weeks = Math.round((days / 7) * 10) / 10;
    if (weeks >= 1) {
      const weekLabel = weeks === 1 ? 'week' : 'weeks';
      return `${weeks} ${weekLabel}`;
    }
    return `${days} days`;
  };

  const formatDateRange = (start?: string | null, end?: string | null) => {
    if (!start && !end) return null;
    const formatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const startLabel = start ? formatter.format(new Date(start)) : '';
    const endLabel = end ? formatter.format(new Date(end)) : '';
    if (!startLabel && !endLabel) return null;
    if (!startLabel) return endLabel;
    if (!endLabel) return startLabel;
    return `${startLabel} - ${endLabel}`;
  };

  const tracks = useMemo(() => {
    const legacyPathways = pricing?.pathways ?? [];
    const list = pricing?.cohorts ?? legacyPathways.filter((pathway) => (pathway.pathway_type || 'track') === 'track');
    return [...list].sort((a, b) => {
      const aTime = a.start_date ? new Date(a.start_date).getTime() : Number.POSITIVE_INFINITY;
      const bTime = b.start_date ? new Date(b.start_date).getTime() : Number.POSITIVE_INFINITY;
      return aTime - bTime;
    });
  }, [pricing]);

  const addons = useMemo(() => {
    const legacyPathways = pricing?.pathways ?? [];
    return pricing?.extensions ?? legacyPathways.filter((pathway) => (pathway.pathway_type || 'track') === 'addon');
  }, [pricing]);

  const allSectionsVisited = visitedSections.size >= sectionOrder.length;

  const handleMarkReviewed = async () => {
    if (!allSectionsVisited) return;
    if (reviewed || savingReview) return;
    try {
      setSavingReview(true);
      await userService.setStepCompletion({ key: 'program_resource_portal', completed: true });
      setReviewed(true);
      setToastMessage('Program portal reviewed');
      if (typeof window !== 'undefined' && user?.id && application?.id) {
        const key = `prospectStepCompletion:${user.id}:${application.id}`;
        const existing = window.localStorage.getItem(key);
        const current = existing ? JSON.parse(existing) : {};
        current.program_resource_portal = true;
        window.localStorage.setItem(key, JSON.stringify(current));
      }
    } catch (error) {
      console.error('Failed to mark portal as reviewed', error);
    } finally {
      setSavingReview(false);
    }
  };

  const handleContinueJourney = () => {
    if (!reviewed) {
      userService
        .setStepCompletion({ key: 'program_resource_portal', completed: true })
        .then(() => {
          setReviewed(true);
          if (typeof window !== 'undefined' && user?.id && application?.id) {
            const key = `prospectStepCompletion:${user.id}:${application.id}`;
            const existing = window.localStorage.getItem(key);
            const current = existing ? JSON.parse(existing) : {};
            current.program_resource_portal = true;
            window.localStorage.setItem(key, JSON.stringify(current));
          }
        })
        .catch((error) => {
          console.error('Failed to mark portal as reviewed', error);
        });
    }
    navigate('/student-pathway#program-playbook');
  };

  const heroTitle = selectedProgram?.title || 'Program Portal';
  const heroLocation = selectedProgram?.location || '';
  const heroHost = selectedProgram?.host_universities?.length
    ? selectedProgram.host_universities.join(', ')
    : '';
  const mockImages = [
    '/media/legacy/2026-002.webp',
    '/media/legacy/2010-013.webp',
    '/media/legacy/2010-009.webp',
    '/media/legacy/2010-015.webp',
    '/media/legacy/2009-003.webp',
    '/media/legacy/2008-006.webp',
  ];
  const heroImage = selectedProgram?.hero_image_url || selectedProgram?.image_url || mockImages[0];

  const portalSummary = selectedProgram?.portal_summary || '';
  const portalBenefits = selectedProgram?.portal_benefits || '';
  const portalAcademics = selectedProgram?.portal_academics || '';
  const portalFaq = selectedProgram?.portal_faq || '';
  const portalCoreOverview = selectedProgram?.portal_core_tracks_overview || '';
  const portalOptionalOverview = selectedProgram?.portal_optional_trips_overview || '';

  const overview = selectedProgram?.overview || selectedProgram?.description || '';
  const mission = selectedProgram?.mission_statement || '';
  const shortTagline = selectedProgram?.short_tagline || '';
  const academicFocus = selectedProgram?.academic_focus || '';
  const cohortClasses = tracks
    .map((track) => ({
      id: track.id ?? track.name,
      name: track.name,
      schedule: [formatDateRange(track.start_date, track.end_date), formatDuration(track)].filter(Boolean).join(' · '),
      classes: (track.classes_offered || []).filter(Boolean),
    }));
  const languageInstruction = selectedProgram?.language_of_instruction || '';
  const languageLevel = selectedProgram?.language_level_required || '';
  const academicFormat = selectedProgram?.academic_format || '';
  const creditInfo = selectedProgram?.credit_or_certificate_info || '';
  const housingType = selectedProgram?.housing_type || '';
  const housingDescription = selectedProgram?.housing_description || '';
  const mealsIncluded = selectedProgram?.meals_included ?? false;
  const foodDescription = selectedProgram?.food_description || '';
  const prayerEnvironment = selectedProgram?.prayer_environment || '';
  const communityLife = selectedProgram?.community_life_description || '';
  const safetyOverview = selectedProgram?.safety_overview || '';
  const weeklyStructure = selectedProgram?.weekly_structure_description || '';
  const culturalImmersion = selectedProgram?.cultural_immersion_description || '';
  const excursions = selectedProgram?.excursions_description || '';
  const includedItems = (selectedProgram?.included_features || []).filter(Boolean);
  const optionalTripsDesc = selectedProgram?.optional_trips_description || '';
  const optionalCoursesDesc = selectedProgram?.optional_courses_description || '';
  const idealParticipant = selectedProgram?.ideal_participant_description || '';
  const notIdeal = selectedProgram?.not_ideal_for_description || '';
  const normalizeGalleryImages = (value: unknown) => {
    if (Array.isArray(value)) {
      return value.map((item) => String(item)).filter(Boolean);
    }
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed.map((item) => String(item)).filter(Boolean);
        }
      } catch {
        // Fall through to split on commas/newlines.
      }
      return value
        .split(/\s*,\s*|\s*\n\s*/g)
        .map((item) => item.trim())
        .filter(Boolean);
    }
    return [];
  };

  const galleryImages = normalizeGalleryImages(selectedProgram?.gallery_image_urls);
  const galleryWithFallback = galleryImages.length > 0 ? galleryImages : mockImages;

  const allPathwayDates = [...tracks, ...addons]
    .map((item) => ({
      start: item.start_date ? new Date(item.start_date).getTime() : null,
      end: item.end_date ? new Date(item.end_date).getTime() : null,
    }))
    .filter((item) => item.start || item.end);
  const earliestStart = allPathwayDates.length
    ? new Date(Math.min(...allPathwayDates.map((item) => item.start || Number.POSITIVE_INFINITY))).toISOString()
    : null;
  const latestEnd = allPathwayDates.length
    ? new Date(Math.max(...allPathwayDates.map((item) => item.end || 0))).toISOString()
    : null;
  const timingRange = formatDateRange(earliestStart, latestEnd);
  const formatWeeks = (days?: number | null) => {
    if (!days) return null;
    if (days % 7 === 0) {
      const weeks = days / 7;
      return `${weeks} week${weeks === 1 ? '' : 's'}`;
    }
    const weeks = Math.round((days / 7) * 10) / 10;
    return `${weeks} weeks`;
  };
  const coreDurations = tracks.map((pathway) => estimateDurationDays(pathway)).filter((days): days is number => Boolean(days));
  const addonDurations = addons.map((pathway) => estimateDurationDays(pathway)).filter((days): days is number => Boolean(days));
  const minCoreDays = coreDurations.length ? Math.min(...coreDurations) : null;
  const totalCoreDays = coreDurations.length ? coreDurations.reduce((sum, days) => sum + days, 0) : null;
  const maxTotalDays = totalCoreDays != null ? totalCoreDays + addonDurations.reduce((sum, days) => sum + days, 0) : null;
  const derivedDuration = minCoreDays && maxTotalDays
    ? `${formatWeeks(minCoreDays)} - ${formatWeeks(maxTotalDays)}`
    : null;
  const heroDuration = derivedDuration || selectedProgram?.duration || '';
  const splitGallery = galleryWithFallback.filter(Boolean);
  const topGallery = splitGallery.slice(0, 3);
  const midGallery = splitGallery.slice(3, 6);

  const [openSection, setOpenSection] = useState('hero');
  const toggleSection = (sectionId: string) => {
    setOpenSection((current) => (current === sectionId ? current : sectionId));
    setVisitedSections((prev) => {
      if (prev.has(sectionId)) return prev;
      const next = new Set(prev);
      next.add(sectionId);
      return next;
    });
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        const target = document.getElementById(`portal-${sectionId}`);
        if (!target) return;
        const sticky = document.getElementById('portal-sticky-header');
        const headerOffset = sticky ? sticky.getBoundingClientRect().height + 16 : 120;
        const targetTop = target.getBoundingClientRect().top + window.scrollY - headerOffset;
        window.scrollTo({ top: targetTop, behavior: 'smooth' });
      });
    }
  };

  const splitLines = (value: string) => (
    value
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
  );

  const buildCalloutItems = (value: string, limit = 3) => (
    splitLines(value).slice(0, limit)
  );

  const extractYouTubeVideoId = (value: string) => {
    const ytMatch = value.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{11})/i);
    return ytMatch ? ytMatch[1] : null;
  };

  const optionalVideoId = [portalOptionalOverview, optionalTripsDesc, optionalCoursesDesc]
    .map((text) => extractYouTubeVideoId(text || ''))
    .find(Boolean);

  const parseFaqPairs = (value: string) => {
    const lines = splitLines(value);
    const pairs: Array<{ question: string; answer: string }> = [];
    let currentQuestion = '';
    let currentAnswer = '';

    const commitPair = () => {
      if (currentQuestion || currentAnswer) {
        pairs.push({ question: currentQuestion, answer: currentAnswer });
      }
      currentQuestion = '';
      currentAnswer = '';
    };

    for (const line of lines) {
      if (/^q\s*:/i.test(line)) {
        commitPair();
        currentQuestion = line.replace(/^q\s*:\s*/i, '').trim();
        continue;
      }
      if (/^a\s*:/i.test(line)) {
        currentAnswer = line.replace(/^a\s*:\s*/i, '').trim();
        continue;
      }

      if (currentAnswer) {
        currentAnswer = `${currentAnswer} ${line}`.trim();
      } else if (currentQuestion) {
        currentQuestion = `${currentQuestion} ${line}`.trim();
      } else {
        currentQuestion = line;
      }
    }

    commitPair();
    return pairs;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 pb-28">
      <div id="portal-sticky-header" className="sticky top-0 z-40 border-b border-slate-200/70 dark:border-slate-800/80 bg-slate-50/90 dark:bg-slate-950/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <nav className="text-xs uppercase tracking-[0.3em] text-emerald-500">
                <button
                  type="button"
                  onClick={() => navigate('/student-pathway')}
                  className="hover:text-emerald-600"
                >
                  Pathway
                </button>
                <span className="mx-2 text-emerald-400">&gt;</span>
                <button
                  type="button"
                  onClick={() => navigate('/student-pathway')}
                  className="hover:text-emerald-600"
                >
                  Exploring
                </button>
                <span className="mx-2 text-emerald-400">&gt;</span>
                <span className="text-emerald-300">Program Portal</span>
              </nav>
              <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 dark:text-white mt-2">Program Portal</h1>
            </div>
            <div className="flex items-center gap-3 text-xs font-semibold text-slate-500 dark:text-slate-300">
              <span>Reading {readingProgress}%</span>
              <div className="h-1.5 w-24 rounded-full bg-slate-200 dark:bg-slate-800">
                <div
                  className="h-1.5 rounded-full bg-emerald-500 transition-[width]"
                  style={{ width: `${readingProgress}%` }}
                />
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate('/student-pathway')}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 dark:border-slate-700 px-4 py-2 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-white dark:hover:bg-slate-900"
            >
              Back to Pathway
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-10 space-y-8">
        <section id="portal-hero" className="scroll-mt-32 rounded-3xl bg-gradient-to-br from-emerald-600 via-green-500 to-teal-500 text-white shadow-xl overflow-hidden">
          <button
            type="button"
            onClick={() => toggleSection('hero')}
            className="w-full px-6 py-4 flex items-center justify-between text-left"
          >
            <div>
              <h2 className="text-2xl sm:text-3xl font-semibold">{heroTitle}</h2>
            </div>
            <span className={`text-emerald-100 transition-transform ${openSection === 'hero' ? 'rotate-180' : ''}`}>
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M5 8l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </button>
          {openSection === 'hero' ? (
            <div className="px-6 pb-6">
              {shortTagline && <p className="text-sm text-emerald-50 mt-2">{shortTagline}</p>}
              {heroImage && (
                <div className="mt-4 overflow-hidden rounded-2xl border border-white/20">
                  <img src={heroImage} alt={heroTitle} className="w-full h-48 object-cover" />
                </div>
              )}
              {(overview || mission) && (
                <div className="mt-4 space-y-2 text-sm text-emerald-50 leading-7 max-w-3xl">
                  {overview && <p>{overview}</p>}
                  {mission && <p className="text-emerald-100">{mission}</p>}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                <div>
                  <p className="text-xs uppercase text-emerald-200">Host University</p>
                  <p className="text-sm font-semibold">{heroHost}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-emerald-200">Duration</p>
                  <p className="text-sm font-semibold">{heroDuration}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-emerald-200">Location</p>
                  <p className="text-sm font-semibold">{heroLocation}</p>
                </div>
              </div>
            </div>
          ) : null}
        </section>

        <section id="portal-overview" className="scroll-mt-32 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
          <button
            type="button"
            onClick={() => toggleSection('overview')}
            className="w-full px-6 py-4 flex items-center justify-between text-left"
          >
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Overview</h3>
            <span className={`text-emerald-500 transition-transform ${openSection === 'overview' ? 'rotate-180' : ''}`}>
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M5 8l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </button>
          {openSection === 'overview' && (portalSummary || portalBenefits) ? (
            <div className="px-6 pb-6 space-y-4 text-sm text-slate-600 dark:text-slate-300 leading-7 max-w-3xl">
              {portalSummary && <p>{portalSummary}</p>}
              {portalBenefits && (
                <ul className="list-disc space-y-2 pl-5 marker:text-emerald-500">
                  {splitLines(portalBenefits).map((benefit) => (
                    <li key={benefit} className="pl-1">{benefit}</li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
        </section>

        <section id="portal-timing" className="scroll-mt-32 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
          <button
            type="button"
            onClick={() => toggleSection('timing')}
            className="w-full px-6 py-4 flex items-center justify-between text-left"
          >
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Timing</h3>
            <span className={`text-emerald-500 transition-transform ${openSection === 'timing' ? 'rotate-180' : ''}`}>
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M5 8l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </button>
          {openSection === 'timing' && (timingRange || heroDuration) ? (
            <div className="px-6 pb-6 space-y-2 text-sm text-slate-600 dark:text-slate-300 leading-7 max-w-3xl">
              {timingRange && <p>Program dates: {timingRange}</p>}
              {heroDuration && <p>Duration: {heroDuration}</p>}
            </div>
          ) : null}
        </section>

        <section id="portal-academics" className="scroll-mt-32 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
          <button
            type="button"
            onClick={() => toggleSection('academics')}
            className="w-full px-6 py-4 flex items-center justify-between text-left"
          >
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Academic Experience</h3>
            <span className={`text-emerald-500 transition-transform ${openSection === 'academics' ? 'rotate-180' : ''}`}>
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M5 8l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </button>
          {openSection === 'academics' && (academicFocus || cohortClasses.length || languageInstruction || languageLevel || academicFormat || creditInfo || portalAcademics) ? (
            <div className="px-6 pb-6 space-y-4 text-sm text-slate-600 dark:text-slate-300">
              <div className="space-y-3 leading-7 max-w-3xl">
                {academicFocus && <p><span className="font-semibold text-slate-900 dark:text-white">Focus:</span> {academicFocus}</p>}
                {cohortClasses.length > 0 && (
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white">Classes by cohort</p>
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                      {cohortClasses.map((cohort) => (
                        <article
                          key={cohort.id}
                          className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/50 p-4"
                        >
                          <p className="text-sm font-semibold text-slate-900 dark:text-white">{cohort.name}</p>
                          {cohort.schedule && (
                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{cohort.schedule}</p>
                          )}
                          {cohort.classes.length > 0 ? (
                            <ul className="mt-3 list-disc pl-5 text-sm text-slate-700 dark:text-slate-300 marker:text-emerald-500 space-y-1">
                              {cohort.classes.map((course) => (
                                <li key={`${cohort.id}-${course}`}>{course}</li>
                              ))}
                            </ul>
                          ) : (
                            <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">Classes coming soon.</p>
                          )}
                        </article>
                      ))}
                    </div>
                  </div>
                )}
                {(languageInstruction || languageLevel) && (
                  <p><span className="font-semibold text-slate-900 dark:text-white">Language:</span> {[languageInstruction, languageLevel].filter(Boolean).join(' · ')}</p>
                )}
                {academicFormat && <p><span className="font-semibold text-slate-900 dark:text-white">Format:</span> {academicFormat}</p>}
                {creditInfo && <p><span className="font-semibold text-slate-900 dark:text-white">Credit/Certificate:</span> {creditInfo}</p>}
                {portalAcademics && <p>{portalAcademics}</p>}
              </div>
              {portalBenefits ? (
                <div className="grid gap-3 sm:grid-cols-3">
                  {buildCalloutItems(portalBenefits).map((item) => (
                    <div key={item} className="rounded-2xl border border-emerald-200/70 bg-emerald-50/60 p-3 text-xs text-emerald-900">
                      {item}
                    </div>
                  ))}
                </div>
              ) : null}
              {topGallery.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {topGallery.map((src) => (
                    <img key={src} src={src} alt="Program gallery" className="h-40 w-full object-cover rounded-2xl border border-slate-200 dark:border-slate-800" />
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </section>

        <section id="portal-life" className="scroll-mt-32 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
          <button
            type="button"
            onClick={() => toggleSection('life')}
            className="w-full px-6 py-4 flex items-center justify-between text-left"
          >
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Life on the Ground</h3>
            <span className={`text-emerald-500 transition-transform ${openSection === 'life' ? 'rotate-180' : ''}`}>
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M5 8l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </button>
          {openSection === 'life' && (housingType || housingDescription || mealsIncluded || foodDescription || prayerEnvironment || communityLife || safetyOverview) ? (
            <div className="px-6 pb-6 space-y-4 text-sm text-slate-600 dark:text-slate-300 leading-7 max-w-3xl">
              {(housingType || housingDescription) && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-400">Housing</p>
                  <p className="mt-2 whitespace-pre-line">{[housingType, housingDescription].filter(Boolean).join(' · ')}</p>
                </div>
              )}
              {(mealsIncluded || foodDescription) && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-400">Meals</p>
                  <p className="mt-2 whitespace-pre-line">
                    {mealsIncluded ? 'Meals included.' : ''} {foodDescription}
                  </p>
                </div>
              )}
              {prayerEnvironment && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-400">Prayer Environment</p>
                  <p className="mt-2 whitespace-pre-line">{prayerEnvironment}</p>
                </div>
              )}
              {communityLife && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-400">Community Life</p>
                  <p className="mt-2 whitespace-pre-line">{communityLife}</p>
                </div>
              )}
              {safetyOverview && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-400">Safety</p>
                  <p className="mt-2 whitespace-pre-line">{safetyOverview}</p>
                </div>
              )}
              <div className="grid gap-3 sm:grid-cols-3">
                {housingType && (
                  <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/60 p-3 text-xs text-slate-600 dark:text-slate-200">
                    Housing: {housingType}
                  </div>
                )}
                {mealsIncluded && (
                  <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/60 p-3 text-xs text-slate-600 dark:text-slate-200">
                    Meals included
                  </div>
                )}
                {prayerEnvironment && (
                  <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/60 p-3 text-xs text-slate-600 dark:text-slate-200">
                    Prayer-friendly setting
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </section>

        <section id="portal-structure" className="scroll-mt-32 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
          <button
            type="button"
            onClick={() => toggleSection('structure')}
            className="w-full px-6 py-4 flex items-center justify-between text-left"
          >
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Program Structure</h3>
            <span className={`text-emerald-500 transition-transform ${openSection === 'structure' ? 'rotate-180' : ''}`}>
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M5 8l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </button>
          {openSection === 'structure' && (weeklyStructure || culturalImmersion || excursions || portalCoreOverview) ? (
            <div className="px-6 pb-6 space-y-4 text-sm text-slate-600 dark:text-slate-300">
              <div className="space-y-3 leading-7 max-w-3xl">
                {weeklyStructure && <p><span className="font-semibold text-slate-900 dark:text-white">Weekly flow:</span> {weeklyStructure}</p>}
                {culturalImmersion && <p><span className="font-semibold text-slate-900 dark:text-white">Cultural immersion:</span> {culturalImmersion}</p>}
                {excursions && <p><span className="font-semibold text-slate-900 dark:text-white">Excursions:</span> {excursions}</p>}
                {portalCoreOverview && <p>{portalCoreOverview}</p>}
              </div>
              {midGallery.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {midGallery.map((src) => (
                    <img key={src} src={src} alt="Program gallery" className="h-40 w-full object-cover rounded-2xl border border-slate-200 dark:border-slate-800" />
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </section>

        <section id="portal-included" className="scroll-mt-32 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
          <button
            type="button"
            onClick={() => toggleSection('included')}
            className="w-full px-6 py-4 flex items-center justify-between text-left"
          >
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white">What’s Included</h3>
            <span className={`text-emerald-500 transition-transform ${openSection === 'included' ? 'rotate-180' : ''}`}>
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M5 8l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </button>
          {openSection === 'included' ? (
            <div className="px-6 pb-6">
              {includedItems.length > 0 ? (
                <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-600 dark:text-slate-300">
                  {includedItems.map((item) => (
                    <li key={item} className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-emerald-500" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
              <div className="mt-6">
                <p className="text-sm font-semibold text-slate-900 dark:text-white">Core Cohorts</p>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  One core cohort is required. You can choose both if you want the full experience.
                </p>
                {pricingLoading ? (
                  <p className="mt-2 text-sm text-slate-500">Loading cohorts...</p>
                ) : tracks.length > 0 ? (
                  <div className="mt-3 space-y-3">
                    {tracks.map((track) => {
                      const dateRange = formatDateRange(track.start_date, track.end_date);
                      const duration = formatDuration(track);
                      return (
                        <div key={track.id ?? track.name} className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4">
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <div>
                              <p className="text-base font-semibold text-slate-900 dark:text-white">{track.name}</p>
                              {(dateRange || duration) && (
                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                  {[dateRange, duration].filter(Boolean).join(' · ')}
                                </p>
                              )}
                            </div>
                          </div>
                          {track.description && (
                            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{track.description}</p>
                          )}
                          {track.included_items?.length ? (
                            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                              Includes: {track.included_items.join(', ')}
                            </p>
                          ) : null}
                          {track.classes_offered?.length ? (
                            <div className="mt-3">
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                Classes
                              </p>
                              <ul className="mt-1 list-disc pl-5 text-sm text-slate-600 dark:text-slate-300 marker:text-emerald-500 space-y-1">
                                {track.classes_offered.map((course) => (
                                  <li key={`${track.id ?? track.name}-${course}`}>{course}</li>
                                ))}
                              </ul>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
        </section>

        <section id="portal-optional" className="scroll-mt-32 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
          <button
            type="button"
            onClick={() => toggleSection('optional')}
            className="w-full px-6 py-4 flex items-center justify-between text-left"
          >
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Optional Experiences</h3>
            <span className={`text-emerald-500 transition-transform ${openSection === 'optional' ? 'rotate-180' : ''}`}>
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M5 8l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </button>
          {openSection === 'optional' && (portalOptionalOverview || optionalTripsDesc || optionalCoursesDesc || addons.length > 0 || pricingLoading) ? (
            <div className="px-6 pb-6 space-y-4 text-sm text-slate-600 dark:text-slate-300 leading-7 max-w-3xl">
              {portalOptionalOverview && <p>{portalOptionalOverview}</p>}
              {optionalTripsDesc && <p>{optionalTripsDesc}</p>}
              {optionalCoursesDesc && <p>{optionalCoursesDesc}</p>}
              {optionalVideoId && (
                <div className="rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                  <div className="aspect-video bg-slate-100 dark:bg-slate-800">
                    <iframe
                      className="h-full w-full"
                      src={`https://www.youtube.com/embed/${optionalVideoId}`}
                      title="Optional experience video"
                      loading="lazy"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    />
                  </div>
                </div>
              )}
              {pricingLoading ? (
                <p className="text-sm text-slate-500">Loading optional experiences...</p>
              ) : addons.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {addons.map((addon) => {
                    const dateRange = formatDateRange(addon.start_date, addon.end_date);
                    const duration = formatDuration(addon);
                    return (
                      <div key={addon.id ?? addon.name} className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <p className="text-base font-semibold text-slate-900 dark:text-white">{addon.name}</p>
                            {(dateRange || duration) && (
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                {[dateRange, duration].filter(Boolean).join(' · ')}
                              </p>
                            )}
                          </div>
                          {addon.capacity != null && (
                            <span className="text-xs text-emerald-600 border border-emerald-200/70 rounded-full px-2 py-0.5">
                              {addon.capacity} seats
                            </span>
                          )}
                        </div>
                        {addon.description && (
                          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{addon.description}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          ) : null}
        </section>

        <section id="portal-profile" className="scroll-mt-32 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
          <button
            type="button"
            onClick={() => toggleSection('profile')}
            className="w-full px-6 py-4 flex items-center justify-between text-left"
          >
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Participant Profile</h3>
            <span className={`text-emerald-500 transition-transform ${openSection === 'profile' ? 'rotate-180' : ''}`}>
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M5 8l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </button>
          {openSection === 'profile' && (idealParticipant || notIdeal) ? (
            <div className="px-6 pb-6 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-600 dark:text-slate-300 leading-7">
              <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/50 p-4">
                <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Ideal for</p>
                {idealParticipant && <p className="mt-2">{idealParticipant}</p>}
              </div>
              <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/50 p-4">
                <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Not ideal for</p>
                {notIdeal && <p className="mt-2">{notIdeal}</p>}
              </div>
            </div>
          ) : null}
        </section>

        <section id="portal-next" className="scroll-mt-32 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
          <button
            type="button"
            onClick={() => toggleSection('next')}
            className="w-full px-6 py-4 flex items-center justify-between text-left"
          >
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white">FAQ / Next Steps</h3>
            <span className={`text-emerald-500 transition-transform ${openSection === 'next' ? 'rotate-180' : ''}`}>
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M5 8l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </button>
          {openSection === 'next' ? (
            <div className="px-6 pb-6">
              {portalFaq ? (
                <div className="mt-2 space-y-4 text-sm text-slate-600 dark:text-slate-300 leading-7 max-w-3xl">
                  {parseFaqPairs(portalFaq).map((item, index) => (
                    <div key={`${item.question}-${index}`}>
                      {item.question && <p><span className="font-semibold text-slate-900 dark:text-white">Q:</span> {item.question}</p>}
                      {item.answer && <p><span className="font-semibold text-slate-900 dark:text-white">A:</span> {item.answer}</p>}
                    </div>
                  ))}
                </div>
              ) : null}
              <div className="mt-6 rounded-2xl border border-emerald-200/70 bg-emerald-50/60 px-4 py-3 text-sm text-emerald-800">
                What happens next: Review the Program Playbook, explore financing support if needed, and begin your application when you’re ready.
              </div>
            </div>
          ) : null}
        </section>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-950/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-slate-500 dark:text-slate-300">
            Mark the portal as reviewed when you finish exploring.
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleMarkReviewed}
              disabled={reviewed || savingReview || !allSectionsVisited}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                reviewed
                  ? 'bg-emerald-100 text-emerald-700 border border-emerald-200 cursor-default'
                  : allSectionsVisited
                    ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            >
              {reviewed ? 'Reviewed' : (savingReview ? 'Saving…' : 'Mark as Reviewed')}
            </button>
            <button
              type="button"
              onClick={handleContinueJourney}
              className="px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-white dark:hover:bg-slate-900"
            >
              Continue Journey
            </button>
          </div>
        </div>
      </div>

      {toastMessage && (
        <div className="fixed bottom-20 right-6 z-50 rounded-xl bg-slate-900 text-white px-4 py-2 text-sm shadow-lg">
          {toastMessage}
        </div>
      )}
    </div>
  );
};

export default ProgramPortalPage;
